<template>
  <v-row justify-center>
    <v-dialog max-width="548" v-model="dialogVisible" persistent>
      <template v-if="state === vueModalState.SELECTING_FILES">
        <v-card>
          <v-card-title class="headline pb-0">
            <div class="headline__heading">
              <div class="headline__title"><IconInfoFilled class="title__icon icon icon-info-filled" />Import 3D objects</div>
              <v-btn class="headline__close" text icon color="grey" @click="onCancelUploadClick()">
                <IconClose class="button__icon icon icon-close" />
              </v-btn>
            </div>
          </v-card-title>
          <v-card-text class="border-bottom" style="font-size: 1rem">
            <v-form class="form--import-object" v-model="isFilesInputValid">
              <fieldset class="fieldset">
                <drag-drop
                  ref="dragdrop"
                  :multiple="true"
                  :accept="allowedModelFileFormats"
                  :isUploading="commencedUpload"
                  @files-selected="onModelFileInputChange"
                >
                </drag-drop>
                <v-card-subtitle v-if="showSldzHint" class="mt-0 pt-0 border-bottom">
                  We noticed you're uploading many .sldasm or .sldprt files. If you would like to import an entire SolidWorks project
                  (.sldasm + .sldprt), please check our
                  <a
                    href="https://community.jig.space/t/importing-3d-models-and-cad-files/13#solidworks"
                    target="_blank"
                    rel="noopener noreferrer"
                    >SolidWorks guidelines</a
                  >.
                </v-card-subtitle>
              </fieldset>
              <fieldset class="fieldset fieldset--tight">
                <div class="modal__subheading">Optimization settings</div>
                <div class="field-row">
                  <div class="switch-group switch-group--tip">
                    <v-switch v-model="autoOptimizeLargeFiles" label="Auto-optimize large files"></v-switch>
                    <div class="switch__hint">
                      <button class="button button--icon button--tip" type="button" @click="toggleTooltip('autoOptimizeTip')">
                        <icon-info class="icon icon-info" />
                      </button>
                      <hint-tooltip
                        ref="autoOptimizeTip"
                        :hasCloseButton="false"
                        :hasArrow="true"
                        arrowPosition="lb"
                        size="sm"
                        position="align-rb"
                        class="switch__tip light"
                      >
                        <template v-slot:copyHtml>
                          Simplifies complex files (750K+ triangles or 1,000+ parts) for smooth performance<br /><br />
                          <a
                            class="tip__link"
                            href="https://www.jig.com/help-center/3d-model-optimization"
                            target="_blank"
                            rel="noopener noreferrer"
                            >Learn more</a
                          >
                        </template>
                      </hint-tooltip>
                    </div>
                  </div>
                </div>
                <div class="field-row">
                  <div class="switch-group switch-group--tip">
                    <v-switch v-model="mergeByMaterialsHierarchy" label="Merge similar objects in hierarchy"></v-switch>
                    <div class="switch__hint">
                      <button class="button button--icon button--tip" type="button" @click="toggleTooltip('mergeObjectsTip')">
                        <icon-info class="icon icon-info" />
                      </button>
                      <hint-tooltip
                        ref="mergeObjectsTip"
                        :hasCloseButton="false"
                        :hasArrow="true"
                        arrowPosition="lb"
                        size="sm"
                        position="align-rb"
                        class="switch__tip light"
                      >
                        <template v-slot:copyHtml>
                          Combines meshes with the same appearance and parent while maintaining visual fidelity<br /><br />
                          <a
                            class="tip__link"
                            href="https://www.jig.com/help-center/3d-model-optimization"
                            target="_blank"
                            rel="noopener noreferrer"
                            >Learn more</a
                          >
                        </template>
                      </hint-tooltip>
                    </div>
                  </div>
                </div>
                <div class="field-row">
                  <div class="switch-group switch-group--decimation">
                    <div class="switch-group--tip">
                      <v-switch v-model="hasDecimation" label="Decimate geometry" @change="onHasDecimationChange"></v-switch>
                      <div class="switch__hint">
                        <button class="button button--icon button--tip" type="button" @click="toggleTooltip('decimateGeometryTip')">
                          <icon-info class="icon icon-info" />
                        </button>
                        <hint-tooltip
                          ref="decimateGeometryTip"
                          :hasCloseButton="false"
                          :hasArrow="true"
                          arrowPosition="lb"
                          size="sm"
                          position="align-rb"
                          class="switch__tip light"
                        >
                          <template v-slot:copyHtml>
                            Reduces triangle count, lower values mean faster performance but less detail<br /><br />
                            <a
                              class="tip__link"
                              href="https://www.jig.com/help-center/3d-model-optimization"
                              target="_blank"
                              rel="noopener noreferrer"
                              >Learn more</a
                            >
                          </template>
                        </hint-tooltip>
                      </div>
                    </div>
                    <v-slider
                      v-show="hasDecimation"
                      v-model="decimationRate"
                      class="decimation-rate align-center"
                      :max="1"
                      :min="0"
                      :step="0.01"
                      hide-details
                    >
                      <template v-slot:append>
                        <jig-text-field v-model="decimationRate" :rules="decimationRateRules"></jig-text-field>
                      </template>
                    </v-slider>
                  </div>
                </div>
              </fieldset>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn class="darkGrey--text" color="secondary" @click="onCancelUploadClick()">Cancel</v-btn>
            <v-btn color="primary" @click="onBeginClick()" :disabled="!isFilesInputValid">Begin upload</v-btn>
          </v-card-actions>
        </v-card>
      </template>
      <template v-else-if="state === vueModalState.UPLOADING_PROCESSING_MANY">
        <v-card>
          <v-card-title class="headline pb-0">
            <div class="headline__heading">
              <div class="headline__title"><IconInfoFilled class="title__icon icon icon-info-filled" />Uploading {{ XOfYMessage }}</div>
              <v-btn class="headline__close" text icon color="grey" @click="onCancelUploadClick()">
                <IconClose class="button__icon icon icon-close" />
              </v-btn>
            </div>
          </v-card-title>
          <v-card-text class="border-bottom overflow-y-auto" style="max-height: 300px">
            <v-list-item dense v-for="(item, i) in selectedFilesWithProgress" :key="i" class="uploading">
              <v-list-item-content class="uploading__item">
                <v-list-item-title class="uploading__file">
                  <div class="uploading__filename">
                    <component :is="getUploadingIconComponent(item.uploadingState)" v-bind="getUploadingIconProps(item.uploadingState)" />
                    {{ `${item.file.name} ${getUploadingStateDisplay(item.uploadingState)}` }}
                  </div>
                  <div v-if="item.uploadingState === uploadingStates.ERROR" class="uploading__filemsg">{{ item.errorMsg }}</div>
                  <div class="uploading__filesize">{{ formatFileSize(item.file.size) }}</div>
                </v-list-item-title>
                <v-list-item-subtitle v-show="item.uploadingState < uploadingStates.DONE" class="uploading__progress">
                  <v-progress-linear
                    v-if="item.uploadingState !== uploadingStates.ERROR"
                    :value="getUploadProgress(item.file)"
                    :indeterminate="item.uploadingState === uploadingStates.PROCESSING"
                  ></v-progress-linear>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-card-text>
          <v-card-actions class="pt-0">
            <v-spacer></v-spacer>
            <v-btn class="darkGrey--text" color="secondary" @click="onCancelUploadClick()">Cancel</v-btn>
            <v-btn color="primary" @click="onTogglePauseUploadClick()" :disabled="!commencedUpload">{{ pauseLabel }}</v-btn>
          </v-card-actions>
        </v-card>
      </template>
      <template v-else-if="state === vueModalState.RESULTS">
        <v-card>
          <v-card-title class="headline pb-0">
            <div class="headline__heading">
              <div class="headline__title"><IconInfoFilled class="title__icon icon icon-info-filled" />Upload complete</div>
              <v-btn class="headline__close" text icon color="grey" @click="closeDialog()">
                <IconClose class="button__icon icon icon-close" />
              </v-btn>
            </div>
          </v-card-title>
          <v-card-text class="border-bottom">
            <v-list-item dense v-for="(item, i) in selectedFilesWithProgress" :key="i" class="uploading">
              <v-list-item-content class="uploading__item">
                <v-list-item-title class="uploading__file">
                  <div class="uploading__filename">
                    <component :is="getUploadingIconComponent(item.uploadingState)" v-bind="getUploadingIconProps(item.uploadingState)" />
                    {{ item.file.name }}
                  </div>
                  <div v-if="item.uploadingState === uploadingStates.ERROR" class="uploading__filemsg">{{ item.errorMsg }}</div>
                  <div v-else class="uploading__filesize">{{ formatFileSize(item.file.size) }}</div>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-card-text>
          <v-card-actions class="pt-0">
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="closeDialog()">Close</v-btn>
          </v-card-actions>
        </v-card>
      </template>
    </v-dialog>
  </v-row>
</template>

<script>
import HintTooltip from '@/components/helper/HintTooltip.vue'
import DragDrop from '@/components/input/DragDrop.vue'
import JigTextField from '@/components/input/JigTextField.vue'
import { AppConst } from '@/constants'
import helpers from '@/modules/library/helpers'
import { VersionConstants } from '@/store/modules/version/constants'
import { ValidationRules } from '@/utils/input-validation'
import { TenantHelpers } from '@/utils/tenant-helpers'
import { segmentEventTracking } from '@/utils/tracking'
import IconCheckCircleFilled from '@svg/IconCheckCircleFilled.svg'
import IconClose from '@svg/IconClose.svg'
import IconCloseFilled from '@svg/IconCloseFilled.svg'
import IconInfo from '@svg/IconInfo.svg'
import IconInfoFilled from '@svg/IconInfoFilled.svg'
import IconUploadFile from '@svg/IconUploadFile.svg'
import { mapActions, mapGetters, mapState } from 'vuex'

const modalState = {
  SELECTING_FILES: 1,
  UPLOADING_PROCESSING_MANY: 4,
  RESULTS: 5,
}

const uploadingState = {
  WAITING: 1,
  UPLOADING: 2,
  UPLOAD_PAUSED: 3,
  PROCESSING: 4,
  DONE: 5,
  ERROR: 6,
}

const defaultOffDecimationRate = 1
const defaultOnDecimationRate = 0.7

Object.freeze(modalState)

export default {
  name: 'UploadModelDialog',
  components: {
    'drag-drop': DragDrop,
    'hint-tooltip': HintTooltip,
    IconCheckCircleFilled,
    IconClose,
    IconCloseFilled,
    IconInfo,
    IconInfoFilled,
    IconUploadFile,
    'jig-text-field': JigTextField,
  },
  data() {
    return {
      dialogVisible: false,
      commencedUpload: false, // Denotes if we selected a file and pressed the begin upload button
      isFilesInputValid: false,
      currentUploadingFileIndex: 0,
      state: modalState.SELECTING_FILES,
      vueModalState: modalState,
      multiUploadJobIndex: -1,
      pauseLabel: 'Pause Upload',
      selectedFilesWithProgress: [
        {
          file: {}, // File type
          uploadProgress: 0,
          uploadingState: uploadingState.WAITING,
          errorMsg: '',
        },
      ],
      uploadingStatesDisplay: {
        1: '(Waiting)',
        2: '(Uploading)',
        3: '(Paused)',
        4: '(Processing)',
        5: '(Done)',
        6: '(Error)',
      },
      cancelAllClicked: false,
      anyUploadSuccessful: false,
      showSldzHint: false,
      latestDesktopVersionName: '',
      autoOptimizeLargeFiles: true,
      mergeByMaterialsHierarchy: true,
      hasDecimation: false,
      decimationRate: defaultOffDecimationRate,
      decimationRateRules: ValidationRules.DecimationRateRules,
    }
  },
  computed: {
    ...mapGetters('app', ['allowedModelFileFormats']),
    ...mapState('app', ['myTenants']),
    ...mapState('library', ['jobs']),
    ...mapState('version', ['latestDesktopVersion']),
    ...mapState('utils', ['apiError']),
    uploadProgress() {
      return Math.round(this.trackedFileUpload.uploadProgressPercentage)
    },
    trackedFileUpload() {
      let trackedUpload = {
        fileHandle: null,
        uploadInProgress: false,
        result: '',
        bytesUploaded: 0,
        bytesTotal: 0,
        uploadProgressPercentage: 0,
        errorMsg: '',
        jigStaffUser: false,
      }
      if (this.currentUploadingFile !== undefined) {
        const result = this.getMultiUploadJobByFile(this.currentUploadingFile)
        if (result.multiUploadJob !== null) {
          trackedUpload = result.multiUploadJob.files[result.uploadIndex]
        }
      }
      return trackedUpload
    },
    trackedFileUploadError() {
      return this.trackedFileUpload.errorMsg
    },
    canCancelUpload() {
      // update this
      return this.uploadProgress < 100
    },
    jobId() {
      let jobId = 0
      if (this.currentUploadingFile !== undefined) {
        const result = this.getMultiUploadJobByFile(this.currentUploadingFile)
        if (result.multiUploadJob !== null) {
          jobId = result.multiUploadJob.job.id
        }
      }
      return jobId
    },
    uploadFinished() {
      let uploadFinished = false
      if (this.currentUploadingFile !== undefined) {
        const result = this.getMultiUploadJobByFile(this.currentUploadingFile)
        if (result.multiUploadJob !== null) {
          const trackedUpload = result.multiUploadJob.files[result.uploadIndex]
          uploadFinished = trackedUpload.finished
        }
      }
      return uploadFinished
    },
    currentUploadingFile() {
      if (this.selectedFilesWithProgress === undefined || this.selectedFilesWithProgress[this.currentUploadingFileIndex] === undefined) {
        return undefined
      }
      return this.selectedFilesWithProgress[this.currentUploadingFileIndex].file
    },
    modelUploadRules() {
      return ValidationRules.ModelUploadFiles
    },
    XOfYMessage() {
      if (this.selectedFilesWithProgress !== undefined && this.selectedFilesWithProgress.length > 1) {
        return `${this.currentUploadingFileIndex + 1} of ${this.selectedFilesWithProgress.length}`
      }
      return ''
    },
    uploadingStates() {
      return uploadingState
    },
    trackingEvent() {
      return {
        action: 'UploadModel',
      }
    },
  },
  watch: {
    // We watch the jobId for changes as this will tell us when the initial file POST has completed
    // successfully and we could start polling the job for a finished/failed state. However we wait until the file upload
    // is complete. We just remember the multi upload job index.
    // TODO: Work out a better way to sync all this together.
    jobId(oldId, newId) {
      if (this.cancelAllClicked) {
        return
      }
      for (const cj of this.jobs) {
        if (cj.job.id === this.jobId) {
          this.multiUploadJobIndex = cj.index
          return
        }
      }
    },
    uploadFinished(oldValue, newValue) {
      if (this.cancelAllClicked) {
        segmentEventTracking('TenantModelUploadCanceled', {
          ...this.trackingEvent,
          fileName: this.selectedFilesWithProgress[this.currentUploadingFileIndex].file.name,
        })
        return
      }
      if (this.uploadFinished) {
        // The file upload has finished. Start polling for job state to be done.
        // Because the job is created before the upload is finished the multiUploadJobIndex should already be set.
        if (this.multiUploadJobIndex !== -1) {
          // Check Errors
          const errMsg = this.getErrorMsgFromFile(this.currentUploadingFile)
          if (errMsg) {
            this.selectedFilesWithProgress[this.currentUploadingFileIndex].errorMsg = errMsg
            this.selectedFilesWithProgress[this.currentUploadingFileIndex].uploadingState = uploadingState.ERROR

            if (this.nextExists()) {
              this.startNext()
              return
            }
          }

          this.selectedFilesWithProgress[this.currentUploadingFileIndex].uploadingState = uploadingState.PROCESSING
          this.waitForJobFinishAndShowResultsOrStartNext()
        } else {
          segmentEventTracking('TenantModelUploadCompleted', {
            ...this.trackingEvent,
            fileName: this.selectedFilesWithProgress[this.currentUploadingFileIndex].file.name,
          })
          consoleLog('Upload finished and no job ID exists yet!')
        }
      }
    },
    trackedFileUploadError(oldValue, newValue) {
      if (this.cancelAllClicked) {
        return
      }
      if (this.trackedFileUploadError !== '') {
        this.state = modalState.RESULTS

        for (const job of this.selectedFilesWithProgress) {
          if (job.uploadingState === uploadingState.DONE) {
            continue
          }

          job.uploadingState = uploadingState.ERROR
          job.errorMsg = this.trackedFileUploadError
        }
      }
    },
  },
  async created() {
    // Dev and test (probably staging too) environment usually don't have Version at all once re-deployed.
    // If no version is available all work will fail on this page, hence redirecting to Versions page
    // to encourage user to at least create a 3.11.0.0 desktop version
    if (this.apiError.indexOf(AppConst.Versions.versionErrorName) >= 0) {
      this.$router.push('/versions')
    }

    if (
      !this.latestDesktopVersion[VersionConstants.VersionDefaults.DefaultApplication] &&
      this.apiError.indexOf(AppConst.Versions.versionErrorName) === -1
    ) {
      await this.getLatestVersionUrl(
        `${VersionConstants.VersionDefaults.DefaultApplication}/${VersionConstants.VersionDefaults.DefaultPlatform}`
      )
    }
  },
  mounted() {
    if (this.latestDesktopVersion[VersionConstants.VersionDefaults.DefaultApplication]) {
      this.latestDesktopVersionName = this.latestDesktopVersion[VersionConstants.VersionDefaults.DefaultApplication].Name
    }
  },
  methods: {
    ...mapActions('library', ['multiUploadJobsClearAll', 'uploadFile', 'pollJobUntilFinalResultAsync', 'multiUploadJobArchive']),
    ...mapActions('version', ['getLatestVersionUrl']),
    getUploadingIcon(fileUploadingState) {
      switch (fileUploadingState) {
        case uploadingState.DONE:
          return {
            icon: 'IconCheckCircleFilled',
            props: { class: 'uploading__icon icon icon-success' },
          }
        case uploadingState.ERROR:
          return {
            icon: 'IconCloseFilled',
            props: { class: 'uploading__icon icon icon-error' },
          }
        default:
          return {
            icon: 'IconUploadFile',
            props: { class: 'uploading__icon icon icon-upload-file' },
          }
      }
    },
    getUploadingIconComponent(fileUploadingState) {
      return this.getUploadingIcon(fileUploadingState).icon
    },
    getUploadingIconProps(fileUploadingState) {
      return this.getUploadingIcon(fileUploadingState).props
    },
    getUploadingStateDisplay(uploadingState) {
      return this.uploadingStatesDisplay[uploadingState]
    },
    getUploadProgress(file) {
      const result = this.getMultiUploadJobByFile(file)
      if (result.multiUploadJob !== null) {
        return Math.round(result.multiUploadJob.files[result.uploadIndex].uploadProgressPercentage)
      } else {
        return 0
      }
    },
    startUploadOfManyFiles() {
      this.state = modalState.UPLOADING_PROCESSING_MANY
      this.commencedUpload = true
      this.$emit('uploading-started')
      this.selectedFilesWithProgress[this.currentUploadingFileIndex].uploadingState = uploadingState.UPLOADING

      this.uploadFile({
        fileToUpload: this.currentUploadingFile,
        latestDesktopVersionName: this.latestDesktopVersionName,
        automaticDecimation: this.autoOptimizeLargeFiles,
        mergeByMaterialsHierarchy: this.mergeByMaterialsHierarchy,
        hasDecimation: this.hasDecimation,
        decimationRate: this.decimationRate,
      })
    },
    async waitForJobFinishAndShowResultsOrStartNext() {
      // This function will keep polling the job until a done state is found.
      segmentEventTracking('TenantModelProcessingStarted', {
        ...this.trackingEvent,
        fileName: this.selectedFilesWithProgress[this.currentUploadingFileIndex].file.name,
      })
      await this.pollJobUntilFinalResultAsync(this.multiUploadJobIndex)
      segmentEventTracking('TenantModelProcessingCompleted', {
        ...this.trackingEvent,
        fileName: this.selectedFilesWithProgress[this.currentUploadingFileIndex].file.name,
      })

      // Check Errors
      const errMsg = this.getErrorMsgFromFile(this.currentUploadingFile)
      if (errMsg) {
        this.selectedFilesWithProgress[this.currentUploadingFileIndex].errorMsg = errMsg
        this.selectedFilesWithProgress[this.currentUploadingFileIndex].uploadingState = uploadingState.ERROR
        // This might be the error from processing so need to track it separately
        segmentEventTracking('TenantModelUploadProcessingErrored', {
          ...this.trackingEvent,
          fileName: this.selectedFilesWithProgress[this.currentUploadingFileIndex].file.name,
        })
      } else {
        this.anyUploadSuccessful = true
        this.selectedFilesWithProgress[this.currentUploadingFileIndex].uploadingState = uploadingState.DONE
        segmentEventTracking('TenantModelUploadCompleted', {
          ...this.trackingEvent,
          fileName: this.selectedFilesWithProgress[this.currentUploadingFileIndex].file.name,
        })
      }
      // After processing finished, CancelAll may have been clicked so check that before continuing
      if (this.cancelAllClicked) {
        segmentEventTracking('TenantModelUploadCanceled', {
          ...this.trackingEvent,
          fileName: this.selectedFilesWithProgress[this.currentUploadingFileIndex].file.name,
        })
        return
      }

      // if there is another file in the selected files list, start uploading that one
      if (this.nextExists()) {
        this.startNext()
        return
      }
      this.state = modalState.RESULTS
    },
    getProcessingStatusForFile(file) {
      const result = this.getMultiUploadJobByFile(file)
      if (result.multiUploadJob !== null) {
        return result.multiUploadJob.job.status.toUpperCase()
      }
      return ''
    },
    // Looks up the multi upload job based on the file path. Designed to allow us to track
    // multiple files being uploaded.
    getMultiUploadJobByFile(file) {
      return helpers.getMultiUploadJobByFilename(this.$store.state.library, file.name)
    },
    getErrorMsgFromFile(file) {
      const multiUploadJobAndIndex = this.getMultiUploadJobByFile(file)

      var msg = ''
      if (multiUploadJobAndIndex.multiUploadJob !== null) {
        const trackedFile = multiUploadJobAndIndex.multiUploadJob.files[multiUploadJobAndIndex.uploadIndex]

        if (trackedFile !== null && trackedFile.errorMsg !== '') {
          msg = `Upload Error: ${trackedFile.errorMsg}` // upload error message
        } else if (multiUploadJobAndIndex.multiUploadJob.job !== null && multiUploadJobAndIndex.multiUploadJob.job.errorMsg !== '') {
          msg = `Processing Error: ${multiUploadJobAndIndex.multiUploadJob.job.errorMsg}` // pipeline or job error message
          if (multiUploadJobAndIndex.multiUploadJob.job.jobMeta && multiUploadJobAndIndex.multiUploadJob.job.jobMeta.pipelineStatus) {
            msg += `\nProcessing Status was: ${multiUploadJobAndIndex.multiUploadJob.job.jobMeta.pipelineStatus}`
          }
        }
      }
      if (msg) {
        consoleLog(`Something went wrong with ${file.name} ${msg}`)
        msg = 'Something went wrong while processing your file.'
      }
      return msg
    },
    nextExists() {
      return this.selectedFilesWithProgress.length - 1 > this.currentUploadingFileIndex
    },
    startNext() {
      this.currentUploadingFileIndex++
      this.commencedUpload = true
      this.$emit('uploading-started')
      this.selectedFilesWithProgress[this.currentUploadingFileIndex].uploadingState = uploadingState.UPLOADING
      // Preserve the same optimization options for every file in this multi-file batch.
      this.uploadFile({
        fileToUpload: this.currentUploadingFile,
        latestDesktopVersionName: this.latestDesktopVersionName,
        automaticDecimation: this.autoOptimizeLargeFiles,
        mergeByMaterialsHierarchy: this.mergeByMaterialsHierarchy,
        hasDecimation: this.hasDecimation,
        decimationRate: this.decimationRate,
      })
    },
    onModelFileInputChange(files) {
      // files is a File[]
      this.showSldzHint = this.isMoreThanOneSolidWorksSelected(files)
      this.currentUploadingFileIndex = 0
      this.selectedFilesWithProgress = files.map((file) => ({
        file,
        uploadProgress: () => {
          const result = this.getMultiUploadJobByFile(this.file)
          if (result.multiUploadJob !== null) {
            return Math.round(result.multiUploadJob.files[result.uploadIndex])
          } else {
            return 0
          }
        },
        uploadingState: uploadingState.WAITING,
        errorMsg: '',
      }))
    },
    onBeginClick() {
      if (this.selectedFilesWithProgress !== null && this.isFilesInputValid) {
        this.startUploadOfManyFiles()
      }
    },
    onTogglePauseUploadClick() {
      const result = this.getMultiUploadJobByFile(this.currentUploadingFile)
      if (result.multiUploadJob !== null) {
        const trackedFile = result.multiUploadJob.files[result.uploadIndex]
        const tusUpload = trackedFile.tusUpload
        if (tusUpload) {
          if (trackedFile.uploadInProgress) {
            tusUpload.abort()
            trackedFile.uploadInProgress = false
            this.selectedFilesWithProgress[this.currentUploadingFileIndex].uploadingState = uploadingState.UPLOAD_PAUSED
            this.pauseLabel = 'Resume Upload'
          } else {
            trackedFile.uploadInProgress = true
            this.selectedFilesWithProgress[this.currentUploadingFileIndex].uploadingState = uploadingState.UPLOADING
            tusUpload.start()
            this.pauseLabel = 'Pause Upload'
          }
        }
      }
    },
    onCancelUploadClick() {
      if (
        this.selectedFilesWithProgress !== null &&
        this.selectedFilesWithProgress !== undefined &&
        this.selectedFilesWithProgress.length > 0 &&
        this.currentUploadingFile
      ) {
        const result = this.getMultiUploadJobByFile(this.currentUploadingFile)
        if (result.multiUploadJob !== null) {
          const trackedFile = result.multiUploadJob.files[result.uploadIndex]
          const tusUpload = trackedFile.tusUpload
          if (tusUpload) {
            if (trackedFile.uploadInProgress) {
              tusUpload.abort()
              trackedFile.uploadInProgress = false
            }
          }
        }
      }
      this.cancelAllClicked = true
      this.closeDialog()
    },
    isMoreThanOneSolidWorksSelected(files) {
      if (files === undefined || files.length === undefined || files.length <= 1) {
        return false
      }
      let foundSolidWorksFileCount = 0
      for (let index = 0; index < files.length; index++) {
        const file = files[index]
        // Lots of checks to handle if files are uploaded without extension or with a single period
        if (file.name !== undefined) {
          let splitstr = file.name.split('.')
          if (splitstr.length > 1) {
            let ext = splitstr.pop().toLowerCase()
            if (ext == 'sldasm' || ext == 'sldprt') {
              foundSolidWorksFileCount++
            }
          }
        }
      }
      return foundSolidWorksFileCount > 1
    },
    closeDialog() {
      if (this.anyUploadSuccessful) {
        this.$emit('upload-success')
      } else {
        this.$emit('uploading-canceled')
      }
      this.dialogVisible = false
      this.autoOptimizeLargeFiles = true
      this.mergeByMaterialsHierarchy = true
      this.hasDecimation = false
      this.decimationRate = defaultOffDecimationRate

      if (this.$refs.dragdrop) {
        this.$refs.dragdrop.clearFiles()
      }
    },
    showDialog() {
      this.multiUploadJobsClearAll()
      this.commencedUpload = false
      this.selectedFilesWithProgress = [{}]
      this.state = modalState.SELECTING_FILES
      this.dialogVisible = true
      this.multiUploadJobIndex = -1
      this.jigStaffUser = false
      this.cancelAllClicked = false
      this.currentUploadingFileIndex = 0
      this.anyUploadSuccessful = false

      for (const tenant of this.myTenants) {
        if (TenantHelpers.IsTenantJigSpaceStaff(tenant)) {
          this.jigStaffUser = true
        }
      }
    },
    onHasDecimationChange() {
      this.decimationRate = this.hasDecimation ? defaultOnDecimationRate : defaultOffDecimationRate
    },
    formatFileSize(bytes) {
      return helpers.formatFileSize(bytes)
    },
    toggleTooltip(refKey) {
      if (this.$refs[refKey] == null) return

      this.$refs[refKey].toggleHintTooltip()
    },
  },
}
</script>

<style lang="scss">
.label + .field-row--flex {
  margin-top: 4px;
}

.fieldset--tight {
  .switch-group {
    padding: 4px;
  }

  .switch-group--decimation {
    padding: 0 4px;
  }
}

.switch-group--decimation {
  display: flex;
  align-items: center;
  gap: 16px;
}

.form--import-object {
  a.tip__link {
    color: var(--brand-primary);
  }

  .hint-tooltip--align-rb {
    @media screen and (max-width: 454px) {
      left: auto;
      right: calc(var(--hint-triangle-bottom) * -1);
    }
  }

  .triangle--lb {
    @media screen and (max-width: 454px) {
      left: auto;
      right: var(--hint-triangle-bottom);
    }
  }
}

.decimation-rate {
  gap: 16px;

  .v-input__control {
    flex-grow: initial;
    width: 102px;
  }

  .v-input__slot {
    padding: 0 4px;
  }

  .v-slider--horizontal {
    margin: 0;
  }

  .v-slider__track-container {
    height: 4px;

    .v-slider__track-background,
    .v-slider__track-fill {
      border-radius: 8px;
    }

    .v-slider__track-background {
      background-color: var(--border-default) !important;
    }
  }

  .v-input__append-outer {
    width: 64px;
    margin: 0;
  }
}

.uploading {
  min-height: 0;
  padding: 0 12px;

  & + .uploading {
    margin-top: 12px;
  }

  .v-list-item__content {
    padding: 0;
  }

  .v-progress-linear {
    border-radius: 8px;
    background-color: var(--border-default);
  }

  .v-progress-linear__background {
    background-color: var(--border-default) !important;
  }

  .v-progress-linear__determinate {
    border-radius: 8px;
  }
}

.uploading__file {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  & > *:not(:last-child) {
    margin-bottom: 0;
  }
}

.uploading__progress {
  margin-top: 4px;
}

.uploading__filename,
.uploading__filesize,
.uploading__filemsg {
  font-size: var(--text-body-md);
}

.uploading__filename {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-subtle);
}

.uploading__filesize {
  color: var(--text-disabled);
}

.uploading__filemsg {
  color: var(--error-primary);
}

.uploading__icon {
  display: block;
  width: 24px;
  height: 24px;
}
</style>
